import { useEffect, useRef } from 'react';

import {
    GEOJSON_BOUNDARY_TYPE,
    GEOJSON_POLYGON_TYPE,
    GEOJSON_PROPERTY
} from 'components/Map/hooks/useFeatures/constants';

import {
    BOUNDARY_LAYERS,
    FLIGHT_HIGHLIGHT_LAYERS,
    FLIGHT_PATH_LAYERS,
    MIRROR_CONTAINER_TEST_ID,
    MIRROR_TEST_ID
} from './constants';
import type {
    DesiredMirror,
    MapboxGeoJSONFeature,
    MirrorEntry,
    Position,
    UseFeatureMirrorsParams
} from './types';

const BOUNDARY_LAYER_SET = new Set<string>(BOUNDARY_LAYERS);
const FLIGHT_LAYER_SET = new Set<string>(FLIGHT_PATH_LAYERS);
const HIGHLIGHT_LAYER_SET = new Set<string>(FLIGHT_HIGHLIGHT_LAYERS);
const QUERY_LAYERS = [...BOUNDARY_LAYERS, ...FLIGHT_PATH_LAYERS, ...FLIGHT_HIGHLIGHT_LAYERS];

const accumulateRingCentroid = (
    ring: ReadonlyArray<Position>,
    sum: { x: number; y: number; n: number }
): void => {
    for (const coord of ring) {
        if (!coord) {
            continue;
        }

        sum.x += coord[0];
        sum.y += coord[1];
        sum.n += 1;
    }
};

const computeAnchor = (feature: MapboxGeoJSONFeature): Position | null => {
    const { geometry } = feature;

    if (!geometry) {
        return null;
    }

    const sum = { n: 0, x: 0, y: 0 };

    if (geometry.type === 'Polygon') {
        const outer = geometry.coordinates[0] as Position[] | undefined;

        if (outer) {
            accumulateRingCentroid(outer, sum);
        }
    } else if (geometry.type === 'MultiPolygon') {
        for (const polygon of geometry.coordinates) {
            const outer = polygon?.[0] as Position[] | undefined;

            if (outer) {
                accumulateRingCentroid(outer, sum);
            }
        }
    } else if (geometry.type === 'LineString') {
        accumulateRingCentroid(geometry.coordinates as Position[], sum);
    } else if (geometry.type === 'MultiLineString') {
        for (const line of geometry.coordinates) {
            accumulateRingCentroid(line as Position[], sum);
        }
    } else if (geometry.type === 'Point') {
        const coord = geometry.coordinates as Position;

        return [coord[0], coord[1]];
    }

    if (sum.n === 0) {
        return null;
    }

    return [sum.x / sum.n, sum.y / sum.n];
};

const buildMirrorNode = (mirror: DesiredMirror): HTMLDivElement => {
    const node = document.createElement('div');

    node.setAttribute('data-testid', mirror.testId);
    node.style.background = 'transparent';
    node.style.height = '1px';
    node.style.left = '0';
    node.style.pointerEvents = 'none';
    node.style.position = 'absolute';
    node.style.top = '0';
    node.style.width = '1px';
    node.style.willChange = 'transform';

    for (const [name, value] of Object.entries(mirror.attrs)) {
        node.setAttribute(name, value);
    }

    return node;
};

const applyTransform = (node: HTMLDivElement, x: number, y: number): void => {
    node.style.transform = `translate3d(${x}px, ${y}px, 0)`;
};

const updateMirrorAttrs = (node: HTMLDivElement, attrs: Record<string, string>): void => {
    for (const [name, value] of Object.entries(attrs)) {
        if (node.getAttribute(name) !== value) {
            node.setAttribute(name, value);
        }
    }
};

const resolveBoundaryTestId = ({
    boundaryType,
    polygonType
}: {
    boundaryType: unknown;
    polygonType: unknown;
}): { shape: 'polygon' | 'circle'; testId: string } | null => {
    const isPolygon = polygonType === GEOJSON_POLYGON_TYPE.POLYGON;
    const isCircle = polygonType === GEOJSON_POLYGON_TYPE.CIRCLE;

    if (!isPolygon && !isCircle) {
        return null;
    }

    if (boundaryType === GEOJSON_BOUNDARY_TYPE.APPLY) {
        return {
            shape: isCircle ? 'circle' : 'polygon',
            testId: isCircle
                ? MIRROR_TEST_ID.APPLY_CIRCLE_BOUNDARY
                : MIRROR_TEST_ID.APPLY_POLYGON_BOUNDARY
        };
    }

    if (boundaryType === GEOJSON_BOUNDARY_TYPE.NO_ENTRY) {
        return {
            shape: isCircle ? 'circle' : 'polygon',
            testId: isCircle
                ? MIRROR_TEST_ID.NO_ENTRY_CIRCLE_BOUNDARY
                : MIRROR_TEST_ID.NO_ENTRY_POLYGON_BOUNDARY
        };
    }

    return null;
};

const pickFlightFeature = (
    features: ReadonlyArray<MapboxGeoJSONFeature>
): Record<string, MapboxGeoJSONFeature> => {
    const chosen: Record<string, { feature: MapboxGeoJSONFeature; isFull: boolean }> = {};

    for (const feature of features) {
        const flightId = feature.properties?.[GEOJSON_PROPERTY.FLIGHT_ID] as string | undefined;

        if (!flightId) {
            continue;
        }

        const isFull = Boolean(feature.properties?.[GEOJSON_PROPERTY.IS_FULL_FLIGHT_PATH]);
        const existing = chosen[flightId];

        if (!existing || (isFull && !existing.isFull)) {
            chosen[flightId] = { feature, isFull };
        }
    }

    const result: Record<string, MapboxGeoJSONFeature> = {};

    for (const [flightId, pick] of Object.entries(chosen)) {
        result[flightId] = pick.feature;
    }

    return result;
};

const useFeatureMirrors = ({
    map,
    mapContainerRef,
    selectedFlightId,
    vertexCoords
}: UseFeatureMirrorsParams): void => {
    const overlayRef = useRef<HTMLDivElement | null>(null);
    const entriesRef = useRef<Record<string, MirrorEntry>>({});
    const syncRef = useRef<VoidFunction>(() => {});
    const rafIdRef = useRef<number | null>(null);

    syncRef.current = () => {
        const overlay = overlayRef.current;

        if (!map || !overlay) {
            return;
        }

        const overlayWidth = overlay.clientWidth;
        const overlayHeight = overlay.clientHeight;

        if (overlayWidth === 0 || overlayHeight === 0) {
            return;
        }

        const desired: Record<string, DesiredMirror> = {};
        const partIndexToBoundaryKey: Record<number, string> = {};

        const allFeatures = map.queryRenderedFeatures({ layers: QUERY_LAYERS });
        const boundaryFeatures: MapboxGeoJSONFeature[] = [];
        const flightFeatures: MapboxGeoJSONFeature[] = [];
        const highlightFeatures: MapboxGeoJSONFeature[] = [];

        for (const feature of allFeatures) {
            const layerId = feature.layer?.id;

            if (!layerId) {
                continue;
            }

            if (BOUNDARY_LAYER_SET.has(layerId)) {
                boundaryFeatures.push(feature);
            } else if (FLIGHT_LAYER_SET.has(layerId)) {
                flightFeatures.push(feature);
            } else if (HIGHLIGHT_LAYER_SET.has(layerId)) {
                highlightFeatures.push(feature);
            }
        }

        for (const feature of boundaryFeatures) {
            const id = feature.properties?.[GEOJSON_PROPERTY.ID] as string | undefined;
            const partIndex = feature.properties?.[GEOJSON_PROPERTY.PART_INDEX] as
                | number
                | undefined;

            if (!id) {
                continue;
            }

            const boundaryType = feature.properties?.[GEOJSON_PROPERTY.BOUNDARY_TYPE];
            const polygonType = feature.properties?.[GEOJSON_PROPERTY.POLYGON_TYPE];
            const resolved = resolveBoundaryTestId({ boundaryType, polygonType });

            if (!resolved) {
                continue;
            }

            const key = partIndex !== undefined ? `boundary:${partIndex}` : `boundary:id:${id}`;

            if (desired[key]) {
                continue;
            }

            const anchor = computeAnchor(feature);

            if (!anchor) {
                continue;
            }

            const projected = map.project(anchor as [number, number]);
            const attrs: Record<string, string> = {
                'data-boundary-type': String(boundaryType),
                'data-feature-id': id,
                'data-shape': resolved.shape
            };

            if (partIndex !== undefined) {
                attrs['data-part-index'] = String(partIndex);
                partIndexToBoundaryKey[partIndex] = key;
            }

            desired[key] = {
                attrs,
                testId: resolved.testId,
                x: projected.x,
                y: projected.y
            };
        }

        const flightChosen = pickFlightFeature(flightFeatures);

        for (const [flightId, feature] of Object.entries(flightChosen)) {
            const anchor = computeAnchor(feature);

            if (!anchor) {
                continue;
            }

            const projected = map.project(anchor as [number, number]);

            desired[`flight:${flightId}`] = {
                attrs: {
                    'data-feature-id': flightId,
                    'data-flight-id': flightId
                },
                testId: MIRROR_TEST_ID.FLIGHT_PATH,
                x: projected.x,
                y: projected.y
            };
        }

        const highlightChosen = pickFlightFeature(highlightFeatures);

        for (const [flightId, feature] of Object.entries(highlightChosen)) {
            if (selectedFlightId && selectedFlightId !== flightId) {
                continue;
            }

            const anchor = computeAnchor(feature);

            if (!anchor) {
                continue;
            }

            const projected = map.project(anchor as [number, number]);

            desired[`highlight:${flightId}`] = {
                attrs: {
                    'data-feature-id': flightId,
                    'data-flight-id': flightId
                },
                testId: MIRROR_TEST_ID.FLIGHT_PATH_HIGHLIGHTED,
                x: projected.x,
                y: projected.y
            };
        }

        for (const part of vertexCoords) {
            const parentKey = partIndexToBoundaryKey[part.partIndex];

            if (!parentKey) {
                continue;
            }

            const ringLength = part.coords.length;
            const isClosedRing =
                ringLength > 1 &&
                part.coords[0]?.[0] === part.coords[ringLength - 1]?.[0] &&
                part.coords[0]?.[1] === part.coords[ringLength - 1]?.[1];
            const effectiveLength = isClosedRing ? ringLength - 1 : ringLength;

            for (let vertexIndex = 0; vertexIndex < effectiveLength; vertexIndex++) {
                const coord = part.coords[vertexIndex];

                if (!coord) {
                    continue;
                }

                const projected = map.project(coord as [number, number]);

                if (
                    projected.x < 0 ||
                    projected.y < 0 ||
                    projected.x > overlayWidth ||
                    projected.y > overlayHeight
                ) {
                    continue;
                }

                desired[`vertex:${part.partIndex}:${vertexIndex}`] = {
                    attrs: {
                        'data-feature-id': part.id,
                        'data-part-index': String(part.partIndex),
                        'data-vertex-index': String(vertexIndex)
                    },
                    parentKey,
                    testId: MIRROR_TEST_ID.BOUNDARY_VERTEX,
                    x: projected.x,
                    y: projected.y
                };
            }
        }

        for (const id of Object.keys(entriesRef.current)) {
            if (!desired[id]) {
                entriesRef.current[id]?.node.remove();
                delete entriesRef.current[id];
            }
        }

        const sortedKeys = Object.keys(desired).sort((a, b) => {
            const aHasParent = desired[a].parentKey !== undefined;
            const bHasParent = desired[b].parentKey !== undefined;

            if (aHasParent === bHasParent) {
                return 0;
            }

            return aHasParent ? 1 : -1;
        });

        for (const key of sortedKeys) {
            const mirror = desired[key];
            const parentEntry = mirror.parentKey ? entriesRef.current[mirror.parentKey] : undefined;
            const parentNode = parentEntry?.node ?? overlay;
            const localX = parentEntry ? mirror.x - parentEntry.screenX : mirror.x;
            const localY = parentEntry ? mirror.y - parentEntry.screenY : mirror.y;
            const existing = entriesRef.current[key];

            if (!existing) {
                const node = buildMirrorNode(mirror);

                parentNode.appendChild(node);
                applyTransform(node, localX, localY);

                entriesRef.current[key] = {
                    node,
                    parentKey: mirror.parentKey,
                    screenX: mirror.x,
                    screenY: mirror.y
                };

                continue;
            }

            if (existing.parentKey !== mirror.parentKey) {
                parentNode.appendChild(existing.node);
                existing.parentKey = mirror.parentKey;
            }

            if (existing.screenX !== mirror.x || existing.screenY !== mirror.y) {
                existing.screenX = mirror.x;
                existing.screenY = mirror.y;
                applyTransform(existing.node, localX, localY);
            }

            updateMirrorAttrs(existing.node, mirror.attrs);
        }
    };

    useEffect(() => {
        if (!map || !mapContainerRef.current) {
            return;
        }

        const overlay = document.createElement('div');

        overlay.setAttribute('aria-hidden', 'true');
        overlay.setAttribute('data-testid', MIRROR_CONTAINER_TEST_ID);
        overlay.style.height = '100%';
        overlay.style.left = '0';
        overlay.style.pointerEvents = 'none';
        overlay.style.position = 'absolute';
        overlay.style.top = '0';
        overlay.style.width = '100%';

        mapContainerRef.current.appendChild(overlay);
        overlayRef.current = overlay;

        const requestSync = () => {
            if (rafIdRef.current !== null) {
                return;
            }

            rafIdRef.current = window.requestAnimationFrame(() => {
                rafIdRef.current = null;
                syncRef.current();
            });
        };

        map.on('idle', requestSync);
        map.on('move', requestSync);
        map.on('zoom', requestSync);

        syncRef.current();

        return () => {
            map.off('idle', requestSync);
            map.off('move', requestSync);
            map.off('zoom', requestSync);

            if (rafIdRef.current !== null) {
                window.cancelAnimationFrame(rafIdRef.current);
                rafIdRef.current = null;
            }

            for (const id of Object.keys(entriesRef.current)) {
                entriesRef.current[id]?.node.remove();
            }

            entriesRef.current = {};

            overlay.remove();
            overlayRef.current = null;
        };
    }, [map, mapContainerRef]);

    useEffect(() => {
        syncRef.current();
    }, [selectedFlightId, vertexCoords]);
};

export default useFeatureMirrors;
